// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, User } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

interface MentionAthletesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorId: string;
  vendorName: string;
  status: 'all' | 'approved' | 'rejected' | 'pending' | 'pending_vendor_approval';
}

interface AthleteInfo {
  id: string;
  name: string;
  username: string;
  avatar_url?: string;
  article_title?: string;
}

const MentionAthletesModal: React.FC<MentionAthletesModalProps> = ({
  open,
  onOpenChange,
  vendorId,
  vendorName,
  status,
}) => {
  const [athletes, setAthletes] = useState<AthleteInfo[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && vendorId) {
      fetchAthletes();
    }
  }, [open, vendorId, status]);

  const fetchAthletes = async () => {
    try {
      setLoading(true);

      // Build the query for profile_mentions
      let query = supabase
        .from('profile_mentions')
        .select('profile_id, source_id')
        .eq('mentioned_by', vendorId)
        .eq('source_type', 'press_article');

      // Filter by status if not 'all'
      if (status !== 'all') {
        query = query.eq('status', status);
      }

      const { data: mentions, error: mentionsError } = await query;

      if (mentionsError) throw mentionsError;
      if (!mentions || mentions.length === 0) {
        setAthletes([]);
        return;
      }

      // Get unique profile IDs and source IDs
      const profileIds = [...new Set(mentions.map(m => m.profile_id))];
      const sourceIds = [...new Set(mentions.map(m => m.source_id))];

      // Fetch athlete profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, username, avatar_url')
        .in('id', profileIds);

      if (profilesError) throw profilesError;

      // Combine the data
      const athleteInfos: AthleteInfo[] = mentions.map(mention => {
        const profile = profiles?.find(p => p.id === mention.profile_id);

        return {
          id: mention.profile_id,
          name: profile?.name || 'Unknown Athlete',
          username: profile?.username || '',
          avatar_url: profile?.avatar_url,
          article_title: 'Unknown Article',
        };
      });

      setAthletes(athleteInfos);
    } catch (error) {
      console.error('Error fetching athletes:', error);
      setAthletes([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'pending':
        return 'Pending';
      case 'pending_vendor_approval':
        return 'Athlete Requests';
      default:
        return 'All';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'approved':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'rejected':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'pending':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'pending_vendor_approval':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{vendorName}'s {getStatusLabel()} Mentions</span>
            <Badge variant="outline" className={getStatusColor()}>
              {athletes.length}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : athletes.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No athletes found
          </div>
        ) : (
          <div className="space-y-4">
            {athletes.map((athlete, index) => (
              <div
                key={`${athlete.id}-${index}`}
                className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <Avatar className="h-12 w-12">
                  <AvatarImage src={athlete.avatar_url} alt={athlete.name} />
                  <AvatarFallback>
                    <User className="h-6 w-6" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm">{athlete.name}</h4>
                    {athlete.username && (
                      <span className="text-xs text-muted-foreground">@{athlete.username}</span>
                    )}
                  </div>
                  {athlete.article_title && (
                    <p className="text-sm text-muted-foreground mt-1 truncate">
                      Article: {athlete.article_title}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MentionAthletesModal;
